/**
 * Convert Contentful CDN URL to Cloudinary URL
 * @param contentfulUrl Contentful CDN URL (images.ctfassets.net)
 * @returns Cloudinary URL
 * @example
 * convertContentfulToCloudinary(
 *   'https://images.ctfassets.net/27bbwmx1ch17/asset-32e3833df8aa45b49971d566a9431dbc/45d38b63c7e0ae0141cc5df82ecde2de/djuren-i-samhallet_al03ty.jpg'
 * )
 * @returns
 * 'https://res.cloudinary.com/djurensratt/image/upload/rdk/djuren-i-samhallet_al03ty.jpg'
 */
export function convertContentfulToCloudinary(contentfulUrl: string, baseUrl?: string): string {
  if (
    !contentfulUrl ||
    (!contentfulUrl.includes('images.ctfassets.net') && !contentfulUrl.includes('ctfassets.net'))
  ) {
    return contentfulUrl
  }

  try {
    // Handle protocol-relative URLs (starting with //)
    const normalizedUrl = contentfulUrl.startsWith('//') ? `https:${contentfulUrl}` : contentfulUrl
    const url = new URL(normalizedUrl)
    const pathParts = url.pathname.split('/')

    // Extract filename from the last part of the path
    let filename = pathParts[pathParts.length - 1]

    // Decode Contentful's hex encoding pattern (_C3_A4 -> ä)
    filename = filename?.replace(/_([0-9A-F]{2})_([0-9A-F]{2})/g, (match, p1, p2) => {
      const bytes = [parseInt(p1, 16), parseInt(p2, 16)]
      // Use TextDecoder for proper UTF-8 decoding in browser
      try {
        const uint8Array = new Uint8Array(bytes)
        const decoder = new TextDecoder('utf-8')
        return decoder.decode(uint8Array)
      } catch (error) {
        // Fallback to manual mapping for common characters
        const hex = p1 + p2
        const charMap: Record<string, string> = {
          C3A4: 'ä', // ä
          C3A5: 'å', // å
          C3B6: 'ö', // ö
          C3A9: 'é', // é
          C3A8: 'è', // è
          C3A0: 'à', // à
          C3BC: 'ü', // ü
          C3B8: 'ø', // ø
          C3A6: 'æ', // æ
        }
        return charMap[hex] || match
      }
    })

    // Also handle standard URL encoding as fallback
    filename = decodeURIComponent(filename || '')

    // Return Cloudinary URL with rdk folder
    const defaultBaseUrl = 'https://res.cloudinary.com/djurensratt/image/upload'
    const cloudinaryBaseUrl = baseUrl || defaultBaseUrl
    return `${cloudinaryBaseUrl}/rdk/${filename}`
  } catch (error) {
    console.warn('Failed to convert Contentful URL to Cloudinary:', contentfulUrl, error)
    return contentfulUrl
  }
}

/**
 * Apply Cloudinary transformations to an image URL
 * @param imageUrl absolute cloudinary url
 * @param transformations
 * @example
 * applyCloudinaryTransformations(
 *   'https://res.cloudinary.com/djurensratt/image/upload/v1623680000/rdk/2021-06-14/2021-06-14-1.jpg',
 *  [
 *    ['c_lfill', 'dpr_auto', 'g_faces'],
 *    ['c_scale', 'dpr_2.0', 'w_1200']
 *  ]
 * )
 * @returns
 * https://res.cloudinary.com/djurensratt/image/upload/c_lfill,dpr_auto,g_faces/c_scale,dpr_2.0,w_1200/v1623680000/rdk/2021-06-14/2021-06-14-1.jpg
 */
export function applyCloudinaryTransformations(
  imageUrl: URL | string,
  transformations: string[][],
) {
  const transformationsString = transformations
    .map((transformation) => transformation.join(','))
    .join('/')
  const url = new URL(imageUrl.toString())
  const [, cloudName, assetType, deliveryType, ...rest] = url.pathname.split('/')
  if (cloudName !== 'djurensratt') {
    console.log(url.pathname)
    throw new Error('Invalid cloudinary cloud name')
  }
  return new URL(
    `/${cloudName}/${assetType}/${deliveryType}/${transformationsString}/${rest.join('/')}`,
    'https://res.cloudinary.com',
  ).toString()
}

/**
 * Drop a stored leading `f_auto/q_auto/` from a public ID. The provider adds its own; left in,
 * they chain a second time and double the derived copies. Anything an editor chose is kept.
 */
export function stripDeliveryTransformations(publicId: string): string {
  const segments = publicId.replace(/^\/+/, '').split('/')
  while (segments.length > 1 && /^(f_auto|q_auto)(,(f_auto|q_auto))*$/.test(segments[0]!)) segments.shift()
  return segments.join('/')
}

const UPLOAD = 'https://res.cloudinary.com/djurensratt/image/upload/'

/**
 * A width-capped srcset for a djurensratt upload URL, or undefined for any other URL. The cap goes
 * after any stored transformations, so a stored crop still applies to the full image.
 */
export function cloudinarySrcset(imageUrl: string, widths: number[]): string | undefined {
  if (!imageUrl.startsWith(UPLOAD)) return undefined
  const segments = imageUrl.slice(UPLOAD.length).split('/')
  // The last segment is always the public ID, even when it reads like `ko_kalv.jpg`.
  const found = segments.slice(0, -1).findIndex((s) => /^v\d+$/.test(s) || !/^[a-z]{1,3}_[^/]*$/.test(s))
  const stored = found === -1 ? segments.length - 1 : found
  const head = segments.slice(0, stored).filter((s) => !/^(f_auto|q_auto)(,(f_auto|q_auto))*$/.test(s))
  const tail = segments.slice(stored)
  return widths
    .map((w) => `${UPLOAD}${[...head, `c_limit,w_${w},f_auto,q_auto:eco`, ...tail].join('/')} ${w}w`)
    .join(', ')
}
